import { Navigate } from 'react-router-dom';

import LoadingCard from '@/components/ui/loading-card';
import { ClientCategorisation } from '@/constants';
import { RouteConfigProvider, useRouteConfig } from '@/context/route-config';
import FuturesClientCategorisationProfessionalAgreement, {
  type FuturesClientCategorisationProfessionalAgreementRouteConfig as FuturesClientCategorisationProfessionalAgreementRouteConfigBase,
} from '@/features/futures/components/client-categorisation-professional-agreement';
import FuturesClientCategorisationLayout from '@/features/futures/layouts/client-categorisation';
import { useProfileClientCategorisation } from '@/hooks/queries/use-profile-client-categorisation';
import { useProfileIdentity } from '@/hooks/queries/use-profile-identity';

interface FuturesClientCategorisationProfessionalAgreementRouteConfig extends FuturesClientCategorisationProfessionalAgreementRouteConfigBase {
  clientCategorisation: string;
}

const FuturesClientCategorisationProfessionalAgreementLoading = () => {
  return (
    <FuturesClientCategorisationLayout progress={0.9}>
      <LoadingCard />
    </FuturesClientCategorisationLayout>
  );
};
FuturesClientCategorisationProfessionalAgreementLoading.displayName = 'FuturesClientCategorisationProfessionalAgreementLoading';

const FuturesClientCategorisationProfessionalAgreementContent = () => {
  const { isLoading: isLoadingIdentity, data: identity } = useProfileIdentity();
  const { isLoading: isLoadingClientCategorisation, data: clientCategorisation } = useProfileClientCategorisation();
  const routeConfig = useRouteConfig<FuturesClientCategorisationProfessionalAgreementRouteConfig>();
  const isLoading = isLoadingIdentity || isLoadingClientCategorisation;
  const countryOfResidency = identity?.addressOfResidency.country;

  if (isLoading) return <FuturesClientCategorisationProfessionalAgreementLoading />;

  if (!countryOfResidency) throw new Error('Unable to proceed with client categorisation, no country of residency');

  if (!clientCategorisation) return <Navigate to={routeConfig.clientCategorisation} replace />;

  if (clientCategorisation !== ClientCategorisation.PROFESSIONAL) {
    return <Navigate to={routeConfig.clientCategorisationTreatment} replace />;
  }

  return (
    <FuturesClientCategorisationLayout progress={0.9}>
      <FuturesClientCategorisationProfessionalAgreement countryOfResidency={countryOfResidency} />
    </FuturesClientCategorisationLayout>
  );
};
FuturesClientCategorisationProfessionalAgreementContent.displayName = 'FuturesClientCategorisationProfessionalAgreementContent';

function FuturesClientCategorisationProfessionalAgreementPage() {
  return (
    <RouteConfigProvider
      config={{
        appropriatenessTest: '/futures/appropriateness-test',
        clientCategorisation: '/futures/client-categorisation',
        clientCategorisationTreatment: '/futures/client-categorisation/treatment',
        jurisdictionUnavailable: '/futures/client-categorisation/jurisdiction-unavailable',
      }}
    >
      <FuturesClientCategorisationProfessionalAgreementContent />
    </RouteConfigProvider>
  );
}
FuturesClientCategorisationProfessionalAgreementPage.displayName = 'FuturesClientCategorisationProfessionalAgreementPage';

export default FuturesClientCategorisationProfessionalAgreementPage;
